import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { CreateUserWithPasswordRequest, User, UserRequest } from './user.interface';

@Injectable({ providedIn: 'root' })
export class UsersService {
  public http = inject(HttpClient);
  private apiUrl = `${environment.apiGatewayUrl}/user`;
  private apiUrlAccount = `${environment.apiGatewayUrl}/account`;

  private usersSubject = new BehaviorSubject<User[]>([]);
  private usersLoaded = false;

  getUsers(): Observable<User[]> {
    if (!this.usersLoaded) {
      this.refreshUsers();
    }
    return this.usersSubject.asObservable();
  }

  refreshUsers(): void {
    this.usersLoaded = true;
    this.http
      .get<User[]>(this.apiUrl)
      .pipe(catchError(this.handleError))
      .subscribe({
        next: (users) => {
          const sortedUsers = users.sort((a, b) =>
            a.username.localeCompare(b.username)
          );
          this.usersSubject.next(sortedUsers);
        },
        error: () => (this.usersLoaded = false),
      });
  }

  getUser(id: string): Observable<User> {
    return this.http
      .get<User>(`${this.apiUrl}/${id}`)
      .pipe(catchError(this.handleError));
  }

  createUser(user: CreateUserWithPasswordRequest): Observable<UserRequest> {
    return this.http
      .post<UserRequest>(this.apiUrl, user)
      .pipe(catchError(this.handleError));
  }

  updateUser(id: string, user: UserRequest): Observable<UserRequest> {
    return this.http
      .put<UserRequest>(`${this.apiUrl}/${id}`, user)
      .pipe(catchError(this.handleError));
  }

  deleteUser(id: string): Observable<void> {
    return this.http
      .delete<void>(`${this.apiUrl}/${id}`)
      .pipe(catchError(this.handleError));
  }

  changePassword(
    id: string,
    passwordChangeRequest: { password: string; confirmPassword: string }
  ): Observable<void> {
    return this.http
      .post<void>(
        `${this.apiUrlAccount}/reset-password/${id}`,
        passwordChangeRequest
      )
      .pipe(catchError(this.handleError));
  }

  sendVerificationEmail(id: string): Observable<void> {
    return this.http
      .get<void>(`${this.apiUrlAccount}/verification-email/${id}`, {})
      .pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
  let errorMsg = 'Ocurrió un error inesperado';

  if (error.error) {
    // Si es un string, usarlo directamente
    if (typeof error.error === 'string') {
      errorMsg = error.error;
    } 
    // Si es un objeto con message
    else if (error.error.message) {
      errorMsg = error.error.message;
    }
  }

  console.error('Error en la API:', error);
  return throwError(() => new Error(errorMsg));
}

}
